import { omit } from 'lodash'
import { BotId } from '@feedyou/schema'

import { getApiUrl } from '../../apiService'
import { SuggestDescriptionQueryArg } from './types'
import { transformLogGroupToNewGroupFormat } from './utils'
import { serverApi, ServerApiTag } from '../../store/serverApi'
import { toastNotifyNlpTrainingError } from '../../components/nlp/toasts'
import { removeAllUnprocessedNlpRowsFromCache, removeUnprocessedNlpRowsFromCache } from './actions'
import {
  NlpLogGroup,
  RowDbDescriptor,
  NlpLogDbPartitionDescriptor
} from '../../components/nlp/TrainingTab/types'
import {
  toastNotifyDeleteNlpLogError,
  toastNotifyDeleteAllNlpLogsError,
  toastNotifyDeleteNlpLogSuccess,
  toastNotifyDeleteAllNlpLogsSuccess
} from '../../components/nlp/TrainingTab/components/TrainingTab/toasts'

export const nlpTrainingApi = serverApi.injectEndpoints({
  endpoints: builder => ({
    getUnprocessedNlpRows: builder.query<
      NlpLogGroup[],
      { botId: BotId; partition: NlpLogDbPartitionDescriptor }
    >({
      query: ({ botId, partition }) => ({
        url: getApiUrl(`/bots/${botId}/nlp/log/unprocessed`),
        headers: { accept: 'application/json' },
        params: { partition: JSON.stringify(partition) }
      }),
      transformResponse: (response: NlpLogGroup[]) => {
        return response.map(transformLogGroupToNewGroupFormat)
      },
      providesTags: (result, error, arg, meta) => {
        return [ServerApiTag.UNPROCESSED_LOG(arg.botId, arg.partition)]
      }
    }),
    deleteUnprocessedUtterance: builder.mutation<
      void,
      {
        botId: BotId
        query: string
        rowsToDelete: RowDbDescriptor[]
        partition: NlpLogDbPartitionDescriptor
      }
    >({
      query: ({ botId, rowsToDelete }) => ({
        method: 'DELETE',
        body: JSON.stringify({ rowsToDelete }),
        headers: { accept: 'application/json', 'Content-Type': 'application/json' },
        url: getApiUrl(`/bots/${botId}/nlp/log/unprocessed`)
      }),
      onQueryStarted: async (arg, api) => {
        try {
          await api.queryFulfilled
          api.dispatch(removeUnprocessedNlpRowsFromCache(arg))
          toastNotifyDeleteNlpLogSuccess(arg.query)
        } catch (e) {
          toastNotifyDeleteNlpLogError(arg.query)
        }
      }
    }),
    processUtterance: builder.mutation<
      void,
      {
        botId: BotId
        query: string
        partition: NlpLogDbPartitionDescriptor
        payload: {
          rowsToProcess: RowDbDescriptor[]
          processedModelId?: string
          processedIntentId?: string
        }
      }
    >({
      query: ({ botId, payload }) => ({
        method: 'POST',
        body: JSON.stringify(payload),
        headers: { accept: 'application/json', 'Content-Type': 'application/json' },
        url: getApiUrl(`/bots/${botId}/nlp/log/process`)
      }),
      onQueryStarted: async (arg, api) => {
        try {
          await api.queryFulfilled
          const { payload, ...restArg } = arg
          const rowsToDelete = payload.rowsToProcess
          api.dispatch(removeUnprocessedNlpRowsFromCache({ ...restArg, rowsToDelete }))
        } catch (e) {
          toastNotifyNlpTrainingError(arg.query)
        }
      }
    }),
    deleteAllUnprocessedUtterance: builder.mutation<
      void,
      { botId: BotId; partition: NlpLogDbPartitionDescriptor }
    >({
      query: ({ botId }) => ({
        method: 'POST',
        headers: { accept: 'application/json' },
        url: getApiUrl(`/bots/${botId}/nlp/log/unprocessed/clear`)
      }),
      onQueryStarted: async (arg, api) => {
        try {
          await api.queryFulfilled
          api.dispatch(removeAllUnprocessedNlpRowsFromCache(arg))
          toastNotifyDeleteAllNlpLogsSuccess()
        } catch (e) {
          toastNotifyDeleteAllNlpLogsError()
        }
      }
    }),
    suggestEnrichedDescription: builder.query<{ textOutput: string }, SuggestDescriptionQueryArg>({
      query: arg => {
        return {
          method: 'POST',
          body: omit(arg, 'botId'),
          url: `/bots/${arg.botId}/nlp/suggest-enriched-description`
        }
      },
      providesTags: [ServerApiTag.SUGGEST_ENRICHED_DESCRIPTION]
    })
  })
})
